import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle2, XCircle, Loader2, KeyRound, ShieldCheck } from 'lucide-react';

// Şifre kuralı kontrolü (Register sayfası ile aynı)
function validatePassword(pw) {
  if (!pw) return [];
  const rules = [
    { id: 'len',   label: 'En az 6 karakter',    ok: pw.length >= 6 },
    { id: 'upper', label: '1 büyük harf',         ok: /[A-Z]/.test(pw) },
    { id: 'lower', label: '1 küçük harf',         ok: /[a-z]/.test(pw) },
    { id: 'spec',  label: '1 özel karakter (%&?!@#$)', ok: /[%&?!@#$]/.test(pw) },
  ];
  return rules;
}

function RuleRow({ ok, label }) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-teal-500 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-gray-300 shrink-0" />}
      <span className={ok ? 'text-teal-600' : 'text-gray-400'}>{label}</span>
    </div>
  );
}

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading | ready | used | error
  const [inviteInfo, setInviteInfo] = useState(null);
  const [mode, setMode] = useState('use'); // 'use' = yönetici şifresini kullan | 'create' = yeni oluştur
  const [pw, setPw] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);
  const [showTempPw, setShowTempPw] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setStatus('error'); return; }
    axios.get(`${API}/auth/invite-info?token=${token}`)
      .then(r => {
        setInviteInfo(r.data);
        // b.1: yönetici şifre girmemişse direkt oluşturma moduna geç
        setMode(r.data.organizer_set_password ? 'use' : 'create');
        setStatus('ready');
      })
      .catch(err => {
        const status = err?.response?.status;
        if (status === 410) setStatus('used');
        else setStatus('error');
      });
  }, [token]);

  const rules = validatePassword(pw);
  const allRulesOk = rules.every(r => r.ok);
  const passwordsMatch = pw === pwConfirm;

  const canSubmit = () => {
    if (mode === 'use') return true; // mevcut şifreyi kullan
    return allRulesOk && passwordsMatch && pw.length > 0;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;
    setSaving(true);
    try {
      const payload = { token };
      if (mode === 'create') payload.new_password = pw;
      const res = await axios.post(`${API}/auth/accept-invite`, payload);
      const { access_token, user } = res.data;
      localStorage.setItem('token', access_token);
      setUser(user);
      toast.success('Hesabınız aktifleştirildi! Hoş geldiniz.');
      navigate('/organizer', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  // ── Yükleniyor ─────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  // ── Hata / Süresi Dolmuş ───────────────────────────────────────────────
  if (status === 'error' || status === 'used') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border p-8 max-w-sm w-full text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-gray-800 mb-2">
            {status === 'used' ? 'Bağlantı Kullanılmış' : 'Geçersiz Bağlantı'}
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            {status === 'used'
              ? 'Bu davet bağlantısı daha önce kullanılmış. Yöneticinizden yeni bir davet isteyin.'
              : 'Bu davet bağlantısı geçersiz veya süresi dolmuş.'}
          </p>
          <Button variant="outline" onClick={() => navigate('/login')} className="w-full">Giriş Sayfasına Git</Button>
        </div>
      </div>
    );
  }

  // ── Hazır ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-100 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 max-w-md w-full" data-testid="set-password-page">

        {/* Logo & Başlık */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Hesabınızı Aktifleştirin</h1>
          <p className="text-sm text-gray-500 mt-1">
            Merhaba <strong>{inviteInfo?.name}</strong>, {inviteInfo?.organizer_name} sizi davet etti.
          </p>
        </div>

        {/* b.2: Yönetici şifre belirledi */}
        {inviteInfo?.organizer_set_password && (
          <div className="mb-6">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-amber-800 font-medium">Yöneticiniz bir şifre belirledi</p>
              <p className="text-xs text-amber-600 mt-0.5">Mevcut şifreyi kullanabilir veya kendi şifrenizi oluşturabilirsiniz.</p>
            </div>

            {/* Mevcut şifre - masked */}
            <div className="space-y-1.5 mb-4">
              <Label className="text-xs text-gray-600">Yönetici Tarafından Belirlenen Şifre</Label>
              <div className="relative">
                <Input
                  type={showTempPw ? 'text' : 'password'}
                  value={inviteInfo?.temp_password || ''}
                  readOnly
                  className="bg-gray-50 pr-10 text-gray-700 font-mono"
                  data-testid="temp-password-display"
                />
                <button type="button" onClick={() => setShowTempPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showTempPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Seçim: Kullan / Değiştir */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                onClick={() => setMode('use')}
                className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${mode === 'use' ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                data-testid="mode-use-btn"
              >
                Şifreyi Kullan
              </button>
              <button
                onClick={() => setMode('create')}
                className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${mode === 'create' ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                data-testid="mode-create-btn"
              >
                Yeni Şifre Oluştur
              </button>
            </div>
          </div>
        )}

        {/* Şifre oluşturma formu (b.1 veya b.2 değiştir seçilince) */}
        {mode === 'create' && (
          <div className="space-y-4 mb-6">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Yeni Şifre</Label>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={pw}
                  onChange={e => setPw(e.target.value)}
                  placeholder="Şifrenizi oluşturun"
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {pw && (
                <div className="space-y-1 pt-1">
                  {rules.map(r => <RuleRow key={r.id} ok={r.ok} label={r.label} />)}
                </div>
              )}
              <p className="text-[10px] text-gray-400 mt-1">En az 6 karakter, 1 büyük harf, 1 küçük harf, 1 özel karakter (%&?!@#$)</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Şifre Tekrar</Label>
              <div className="relative">
                <Input
                  type={showPwConfirm ? 'text' : 'password'}
                  value={pwConfirm}
                  onChange={e => setPwConfirm(e.target.value)}
                  placeholder="Şifrenizi tekrar girin"
                  className={`pr-10 ${pwConfirm && !passwordsMatch ? 'border-red-400' : ''}`}
                  data-testid="new-password-confirm-input"
                />
                <button type="button" onClick={() => setShowPwConfirm(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPwConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {pwConfirm && !passwordsMatch && (
                <p className="text-xs text-red-500">Şifreler uyuşmuyor</p>
              )}
            </div>
          </div>
        )}

        {/* Gönder butonu */}
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit() || saving}
          className="w-full bg-teal-600 hover:bg-teal-700 h-11 font-semibold"
          data-testid="accept-invite-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <ShieldCheck className="w-4 h-4 mr-2" />
          )}
          {mode === 'use' ? 'Hesabı Aktifleştir ve Giriş Yap' : 'Şifremi Oluştur ve Giriş Yap'}
        </Button>

        <p className="text-center text-xs text-gray-400 mt-4">
          Hesabınız var mı?{' '}
          <button onClick={() => navigate('/login')} className="text-teal-600 hover:underline">Giriş yapın</button>
        </p>
      </div>
    </div>
  );
}
